/*
PROV: COHINT001.BF.K4FBA8TCLI
REQ: SYS-ARCH-18, AUTH-19
WHY: Firebase client SDK integration for token management. Exchanges custom
     tokens (from API OTP verify) for Firebase ID tokens, provides ID tokens
     for API request authorization, and manages Firebase auth state.
*/

import { initializeApp, type FirebaseApp } from "firebase/app";
import {
  getAuth,
  signInWithCustomToken,
  signOut,
  onAuthStateChanged,
  connectAuthEmulator,
  type Auth,
  type User,
} from "firebase/auth";

// ---------------------------------------------------------------------------
// Firebase config — resolved at runtime from window.__COHORTA_RUNTIME__
// (injected server-side by _document.jsx from Cloud Run env vars) with
// build-time NEXT_PUBLIC_* fallbacks for local dev / tests.
// ---------------------------------------------------------------------------
declare global {
  interface Window {
    __COHORTA_RUNTIME__?: Record<string, string>;
  }
}

function runtimeVar(name: string, fallback: string): string {
  if (typeof window !== "undefined") {
    const val = window.__COHORTA_RUNTIME__?.[name];
    if (val) return val;
  }
  return fallback;
}

function resolveAuthEmulatorHost(): string {
  const configured = runtimeVar("NEXT_PUBLIC_FIREBASE_AUTH_EMULATOR_HOST", "").trim();
  if (configured) return configured;

  if (typeof window === "undefined") return "";
  const host = window.location.hostname;
  const isLocalHost = host === "localhost" || host === "127.0.0.1";
  const usingDemoConfig = firebaseConfig.apiKey === "demo-key" || firebaseConfig.projectId === "demo-cohorta";

  if (isLocalHost && usingDemoConfig) return "127.0.0.1:9099";
  return "";
}

const firebaseConfig = {
  apiKey: runtimeVar("NEXT_PUBLIC_FIREBASE_API_KEY", "demo-key"),
  projectId: runtimeVar("NEXT_PUBLIC_FIREBASE_PROJECT_ID", "demo-cohorta"),
  authDomain: runtimeVar("NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN", "localhost"),
};

let _app: FirebaseApp | null = null;
let _auth: Auth | null = null;
let _emulatorConnected = false;

// Promise that resolves once Firebase auth has restored its persisted session.
// Without this, `auth.currentUser` is null on initial page load until the
// async session restore completes, causing 401s on direct navigations.
let _authReady: Promise<void> | null = null;

function getFirebaseAuth(): Auth {
  if (_auth) return _auth;

  _app = initializeApp(firebaseConfig);
  _auth = getAuth(_app);

  // Connect to emulator in development
  const emulatorHost = resolveAuthEmulatorHost();
  if (emulatorHost && !_emulatorConnected) {
    _emulatorConnected = true;
    connectAuthEmulator(_auth, `http://${emulatorHost}`, {
      disableWarnings: true,
    });
  }

  // Create a one-time promise that settles once auth state is known.
  const auth = _auth;
  _authReady = new Promise<void>((resolve) => {
    const unsubscribe = onAuthStateChanged(auth, () => {
      unsubscribe();
      resolve();
    });
  });

  return _auth;
}

/**
 * Exchange a custom token (from API OTP verify) for a Firebase session.
 * After this call, getIdToken() returns a valid ID token.
 */
export async function exchangeCustomToken(customToken: string): Promise<User> {
  const auth = getFirebaseAuth();
  const credential = await signInWithCustomToken(auth, customToken);
  return credential.user;
}

/**
 * Get the current user's ID token for API requests.
 * Returns null if not signed in.
 *
 * Firebase automatically refreshes expired tokens (tokens live 1 hour).
 * The forceRefresh parameter forces a new token from Firebase servers.
 */
export async function getIdToken(
  forceRefresh = false,
): Promise<string | null> {
  const auth = getFirebaseAuth();

  // Wait for Firebase to restore the persisted session before checking
  // currentUser, otherwise we return null on initial page loads.
  if (_authReady) await _authReady;

  const user = auth.currentUser;
  if (!user) return null;
  return user.getIdToken(forceRefresh);
}

/**
 * Sign out — clears the Firebase session locally.
 * Call after the API /logout endpoint succeeds.
 */
export async function firebaseSignOut(): Promise<void> {
  const auth = getFirebaseAuth();
  await signOut(auth);
}

/**
 * Subscribe to auth state changes.
 * Useful for showing loading states during token refresh.
 */
export function onAuthChange(
  callback: (user: User | null) => void,
): () => void {
  const auth = getFirebaseAuth();
  return onAuthStateChanged(auth, callback);
}

/**
 * Check if a user is currently signed in.
 */
export function isSignedIn(): boolean {
  const auth = getFirebaseAuth();
  return auth.currentUser !== null;
}

/**
 * Get the underlying Auth instance (advanced use only).
 */
export { type User } from "firebase/auth";
